import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Text,
  Button,
  Group,
  Stack,
  Box,
  Paper,
  TextInput,
  Checkbox,
  Modal,
  Loader,
  Flex,
  FileButton,
  Alert,
} from "@mantine/core";
import {
  IconUserCheck,
  IconPlus,
  IconEdit,
  IconTrash,
  IconUpload,
  IconPlayerPlay,
  IconFileText,
  IconStar,
  IconShieldCheck,
  IconAlertTriangle,
  IconListCheck,
  IconBook,
  IconTrophy,
  IconSchool,
  IconUsers,
  IconCalendarEvent,
  IconQuestionMark,
  IconCircleCheck,
  IconChevronLeft,
  IconChevronRight,
  IconGripVertical,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import "../Organization/OrganizationPage.css";
import "./ProgramDetailsPage.css";
import "./QuizBuilder.css";

const ADMIN_SECTIONS = [
  {
    id: "courses",
    label: "Training Courses",
    icon: IconBook,
    path: "/organization?tab=courses",
  },
  {
    id: "incentives",
    label: "Incentives",
    icon: IconTrophy,
    path: "/organization?tab=incentives",
  },
  { id: "programs", label: "Programs", icon: IconSchool, path: "/programs" },
  {
    id: "program-creation",
    label: "Program Creation",
    icon: IconPlus,
    path: null,
  },
  {
    id: "groups",
    label: "Custom Groups",
    icon: IconUsers,
    path: "/organization?tab=groups",
  },
  {
    id: "events",
    label: "Events",
    icon: IconCalendarEvent,
    path: "/reps/downline?tab=events",
  },
  {
    id: "documents",
    label: "Documents",
    icon: IconFileText,
    path: "/organization?tab=documents",
  },
];

// ─── Task type options ────────────────────────────────────────────────────────
const TASK_TYPES = [
  {
    label: "Self-Complete",
    value: "USER_APPROVE",
    Icon: IconUserCheck,
    color: "#22c55e",
    bg: "#f0fdf4",
  },
  {
    label: "Text Response",
    value: "TEXT_RESPONSE",
    Icon: IconFileText,
    color: "#8b5cf6",
    bg: "#f5f3ff",
  },
  {
    label: "Complete Video",
    value: "VIDEO",
    Icon: IconPlayerPlay,
    color: "#3b82f6",
    bg: "#eff6ff",
  },
  {
    label: "Pitch Review",
    value: "PITCH_REVIEW",
    Icon: IconStar,
    color: "#d946ef",
    bg: "#fdf4ff",
  },
  {
    label: "Upload File",
    value: "UPLOAD",
    Icon: IconUpload,
    color: "#f59e0b",
    bg: "#fef3c7",
  },
  {
    label: "Quiz",
    value: "QUIZ",
    Icon: IconQuestionMark,
    color: "#06b6d4",
    bg: "#ecfeff",
  },
];

// ─── Video Search Select ──────────────────────────────────────────────────────
function VideoSearchSelect({ videos, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = React.useRef(null);

  const selected = videos.find(v => v.id.toString() === value);

  const getCourseName = v =>
    v.course?.course_title || v.course_name || v.course_title || "";

  const filtered = query.trim()
    ? videos.filter(
        v =>
          v.video_title.toLowerCase().includes(query.toLowerCase()) ||
          getCourseName(v).toLowerCase().includes(query.toLowerCase())
      )
    : videos;

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Trigger / search input */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "6px 10px",
          background: "white",
          cursor: "text",
          minHeight: 44,
        }}
        onClick={() => setOpen(true)}
      >
        {selected && !open ? (
          <>
            {selected.video_thumbnail && (
              <img
                src={selected.video_thumbnail}
                alt=""
                style={{
                  width: 40,
                  height: 26,
                  objectFit: "cover",
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#111827",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selected.video_title}
              </div>
              {getCourseName(selected) && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getCourseName(selected)}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onChange("", null);
                setQuery("");
              }}
              style={{
                background: "none",
                border: "none",
                color: "#9ca3af",
                cursor: "pointer",
                fontSize: 16,
                lineHeight: 1,
                padding: 0,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </>
        ) : (
          <input
            autoFocus={open}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search videos..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: "0.875rem",
              color: "#111827",
              background: "transparent",
            }}
          />
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 200,
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "12px 14px",
                color: "#9ca3af",
                fontSize: "0.875rem",
              }}
            >
              No videos found
            </div>
          ) : (
            filtered.map(v => (
              <button
                key={v.id}
                type="button"
                onClick={() => {
                  onChange(v.id.toString(), v);
                  setQuery("");
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "8px 12px",
                  border: "none",
                  background:
                    v.id.toString() === value ? "#eff6ff" : "transparent",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {v.video_thumbnail ? (
                  <img
                    src={v.video_thumbnail}
                    alt=""
                    style={{
                      width: 52,
                      height: 34,
                      objectFit: "cover",
                      borderRadius: 5,
                      flexShrink: 0,
                      background: "#f3f4f6",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 52,
                      height: 34,
                      borderRadius: 5,
                      background: "#f3f4f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IconPlayerPlay size={14} color="#9ca3af" />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {v.video_title}
                  </div>
                  {getCourseName(v) && (
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#6b7280",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {getCourseName(v)}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inline Task Form ─────────────────────────────────────────────────────────
function buildFormState(task) {
  const src = task && typeof task === "object" ? task : {};
  const rawAnswers = Array.isArray(src.quiz_answers) ? src.quiz_answers : [];
  // Multi-question: build from quiz_questions if present
  const rawQuizQuestions =
    Array.isArray(src.quiz_questions) && src.quiz_questions.length > 0
      ? src.quiz_questions
      : null;
  return {
    name: src.name || "",
    description: src.description || "",
    type: src.type || "USER_APPROVE",
    video_id: src.video_id || "",
    videoTitle: src.videoTitle || "",
    requires_image: !!src.requires_image,
    requires_admin_approval: !!src.requires_admin_approval,
    // Legacy single-question fields (kept for backward compat)
    quiz_question: src.quiz_question || "",
    quiz_answers: rawAnswers.length >= 2 ? rawAnswers : ["", ""],
    quiz_correct_answer: src.quiz_correct_answer ?? 0,
    // Multi-question fields
    quiz_questions: rawQuizQuestions || [
      { question: "", answers: ["", ""], correct_answer_index: 0 },
    ],
    quiz_passing_score: src.quiz_passing_score ?? 75,
    quiz_cooldown_minutes: src.quiz_cooldown_minutes ?? 0,
    // Existing file attachments from API (program_task_files relation)
    existing_files: Array.isArray(src.files)
      ? src.files
          .filter(f => !f.deleted_at)
          .map(f => ({
            id: f.id,
            file_name: f.file_name || null,
            file_url: f.file_url || null,
          }))
      : // Fall back to legacy single-file fields
        src.file_url
        ? [
            {
              id: null,
              file_name: src.file_name || null,
              file_url: src.file_url,
            },
          ]
        : [],
  };
}

function InlineTaskForm({ onSave, onCancel, task, videos, loadingVideos }) {
  const [form, setForm] = useState(buildFormState(task));
  const [showDetails, setShowDetails] = useState(
    !!(
      task &&
      (task.description ||
        task.requires_image ||
        task.requires_admin_approval ||
        (task.type === "VIDEO" && task.video_id) ||
        task.type === "QUIZ")
    )
  );
  const [typeOpen, setTypeOpen] = useState(false);
  const [activeQuestionIdx, setActiveQuestionIdx] = useState(0);
  const [taskFiles, setTaskFiles] = useState([]);
  const nameRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (nameRef.current) nameRef.current.focus();
  }, []);

  const handleKeyDown = e => {
    if (e.key === "Enter" && form.name.trim()) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") onCancel();
  };

  const handleSave = () => {
    if (form.type === "VIDEO") {
      if (!form.video_id) {
        setShowDetails(true);
        return;
      }
      // Auto-generate name from video title if not already set
      if (!form.name.trim() && form.videoTitle) {
        const autoName = `Watch ${form.videoTitle}`;
        onSave({
          ...form,
          name: autoName,
          files: taskFiles,
          fileName: taskFiles[taskFiles.length - 1]?.name || null,
        });
        return;
      }
    }
    if (form.type === "QUIZ") {
      // Validate multi-question quiz
      const validQuestions = form.quiz_questions.filter(
        q => q.question.trim() && q.answers.filter(a => a.trim()).length >= 2
      );
      if (validQuestions.length === 0) {
        setShowDetails(true);
        return;
      }
      // Strip empty answers and use first question as task name
      const cleanedQuestions = form.quiz_questions
        .filter(q => q.question.trim())
        .map(q => ({
          ...q,
          answers: q.answers.filter(a => a.trim()),
        }));
      const taskName =
        form.name.trim() || cleanedQuestions[0]?.question || "Quiz";
      onSave({
        ...form,
        name: taskName,
        quiz_questions: cleanedQuestions,
        files: taskFiles,
        fileName: taskFiles[taskFiles.length - 1]?.name || null,
      });
      return;
    }
    if (!form.name.trim()) return;
    onSave({
      ...form,
      files: taskFiles,
      fileName: taskFiles[taskFiles.length - 1]?.name || null,
    });
  };

  const selectedType =
    TASK_TYPES.find(t => t.value === form.type) || TASK_TYPES[0];

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "16px 20px",
        background: "white",
        marginBottom: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      {/* Row 1: type picker + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {/* Type dropdown trigger */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setTypeOpen(o => !o)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 10px",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              background: selectedType.bg,
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: selectedType.color,
              whiteSpace: "nowrap",
            }}
          >
            <selectedType.Icon size={15} />
            <span>{selectedType.label}</span>
            <span style={{ fontSize: 10, color: "#9ca3af" }}>▾</span>
          </button>
          {typeOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 100,
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                minWidth: 180,
                overflow: "hidden",
              }}
            >
              {TASK_TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => {
                    setForm(p => ({
                      ...p,
                      type: t.value,
                      video_id: "",
                      videoTitle: "",
                      requires_image:
                        t.value === "USER_APPROVE" ||
                        t.value === "TEXT_RESPONSE"
                          ? p.requires_image
                          : false,
                      requires_admin_approval:
                        t.value === "PITCH_REVIEW"
                          ? true
                          : t.value === "USER_APPROVE" ||
                              t.value === "TEXT_RESPONSE" ||
                              t.value === "UPLOAD"
                            ? p.requires_admin_approval
                            : false,
                    }));
                    // Auto-expand details for types that need extra config
                    if (t.value === "QUIZ" || t.value === "VIDEO") {
                      setShowDetails(true);
                    }
                    setTypeOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "10px 14px",
                    border: "none",
                    background: form.type === t.value ? t.bg : "transparent",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: form.type === t.value ? 600 : 400,
                    color: form.type === t.value ? t.color : "#374151",
                    textAlign: "left",
                  }}
                >
                  <t.Icon
                    size={15}
                    color={form.type === t.value ? t.color : "#6b7280"}
                  />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Task name input — hidden for QUIZ and VIDEO (auto-named) */}
        {form.type !== "QUIZ" && form.type !== "VIDEO" && (
          <input
            ref={nameRef}
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="Task name..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: "0.9375rem",
              color: "#1a1a1a",
              background: "#f9fafb",
              borderRadius: 8,
              padding: "8px 12px",
            }}
          />
        )}
        {form.type === "VIDEO" && (
          <span
            style={{
              flex: 1,
              fontSize: "0.9375rem",
              color: form.videoTitle ? "#111827" : "#9ca3af",
              fontWeight: form.videoTitle ? 600 : 400,
              padding: "8px 12px",
            }}
          >
            {form.videoTitle
              ? `Watch ${form.videoTitle}`
              : "Select a video below…"}
          </span>
        )}
        {form.type === "QUIZ" && (
          <input
            ref={nameRef}
            value={form.quiz_question}
            onChange={e =>
              setForm(p => ({
                ...p,
                quiz_question: e.target.value,
                name: e.target.value,
              }))
            }
            onKeyDown={handleKeyDown}
            placeholder="Quiz question..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: "0.9375rem",
              color: "#1a1a1a",
              background: "#f9fafb",
              borderRadius: 8,
              padding: "8px 12px",
            }}
          />
        )}
      </div>

      {/* Row 2: expanded details (description / video / quiz answers) */}
      {(showDetails || form.type === "QUIZ") && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {form.type === "VIDEO" &&
            (loadingVideos ? (
              <Flex align="center" gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading videos...</Text>
              </Flex>
            ) : (
              <VideoSearchSelect
                videos={videos}
                value={form.video_id}
                onChange={(value, video) => {
                  const autoName = video ? `Watch ${video.video_title}` : "";
                  setForm(p => ({
                    ...p,
                    video_id: value || "",
                    videoTitle: video?.video_title || "",
                    name: autoName,
                  }));
                }}
              />
            ))}

          {form.type === "QUIZ" && (
            <div className="quiz-builder-container">
              {/* Quiz header with icon and title */}
              <div className="quiz-header">
                <div className="quiz-icon">?</div>
                <input
                  className="quiz-title-input"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Enter quiz title..."
                />
              </div>

              {/* Single question card */}
              {(() => {
                const qIdx = Math.min(
                  activeQuestionIdx,
                  form.quiz_questions.length - 1
                );
                const q = form.quiz_questions[qIdx];
                return (
                  <div className="quiz-question-card">
                    {form.quiz_questions.length > 1 && (
                      <div className="question-header">
                        <span className="question-number">
                          Question {qIdx + 1}
                        </span>
                        <button
                          type="button"
                          className="question-remove-btn"
                          onClick={() => {
                            const newQs = form.quiz_questions.filter(
                              (_, i) => i !== qIdx
                            );
                            setForm(p => ({ ...p, quiz_questions: newQs }));
                            setActiveQuestionIdx(i =>
                              Math.min(i, newQs.length - 1)
                            );
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    <input
                      className="question-input"
                      value={q.question}
                      onChange={e => {
                        const newQs = [...form.quiz_questions];
                        newQs[qIdx] = {
                          ...newQs[qIdx],
                          question: e.target.value,
                        };
                        setForm(p => ({
                          ...p,
                          quiz_questions: newQs,
                        }));
                      }}
                      placeholder="Enter question..."
                    />
                    <span className="answer-options-label">
                      Answer Options (click circle = correct)
                    </span>
                    {q.answers.map((answer, aIdx) => (
                      <div key={aIdx} className="answer-option-row">
                        <button
                          type="button"
                          className={`answer-radio ${q.correct_answer_index === aIdx ? "selected" : ""}`}
                          onClick={() => {
                            const newQs = [...form.quiz_questions];
                            newQs[qIdx] = {
                              ...newQs[qIdx],
                              correct_answer_index: aIdx,
                            };
                            setForm(p => ({ ...p, quiz_questions: newQs }));
                          }}
                        >
                          <div className="answer-radio-inner" />
                        </button>
                        <input
                          className={`answer-input ${q.correct_answer_index === aIdx ? "correct" : ""}`}
                          value={answer}
                          onChange={e => {
                            const newQs = [...form.quiz_questions];
                            const newAnswers = [...newQs[qIdx].answers];
                            newAnswers[aIdx] = e.target.value;
                            newQs[qIdx] = {
                              ...newQs[qIdx],
                              answers: newAnswers,
                            };
                            setForm(p => ({ ...p, quiz_questions: newQs }));
                          }}
                          placeholder={`Option ${aIdx + 1}${aIdx < 2 ? " (required)" : ""}`}
                        />
                        {q.answers.length > 2 && (
                          <button
                            type="button"
                            className="answer-remove-btn"
                            onClick={() => {
                              const newQs = [...form.quiz_questions];
                              const newAnswers = newQs[qIdx].answers.filter(
                                (_, i) => i !== aIdx
                              );
                              const newCorrect =
                                newQs[qIdx].correct_answer_index >=
                                newAnswers.length
                                  ? 0
                                  : newQs[qIdx].correct_answer_index;
                              newQs[qIdx] = {
                                ...newQs[qIdx],
                                answers: newAnswers,
                                correct_answer_index: newCorrect,
                              };
                              setForm(p => ({ ...p, quiz_questions: newQs }));
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-option-btn"
                      onClick={() => {
                        const newQs = [...form.quiz_questions];
                        newQs[qIdx] = {
                          ...newQs[qIdx],
                          answers: [...newQs[qIdx].answers, ""],
                        };
                        setForm(p => ({ ...p, quiz_questions: newQs }));
                      }}
                    >
                      <IconPlus size={11} /> Add option
                    </button>
                  </div>
                );
              })()}

              {/* Navigation at bottom with numbered buttons and plus */}
              <div className="quiz-navigation">
                <button
                  type="button"
                  className="quiz-nav-arrow"
                  onClick={() => setActiveQuestionIdx(i => Math.max(0, i - 1))}
                  disabled={activeQuestionIdx === 0}
                >
                  <IconChevronLeft size={14} />
                </button>
                {form.quiz_questions.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`quiz-nav-number ${idx === activeQuestionIdx ? "active" : ""}`}
                    onClick={() => setActiveQuestionIdx(idx)}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="quiz-nav-add"
                  disabled={
                    form.quiz_questions.length >= 20 ||
                    form.quiz_questions.some(
                      q =>
                        !q.question.trim() ||
                        q.answers.filter(a => a.trim()).length < 2
                    )
                  }
                  onClick={() => {
                    setForm(p => ({
                      ...p,
                      quiz_questions: [
                        ...p.quiz_questions,
                        {
                          question: "",
                          answers: ["", ""],
                          correct_answer_index: 0,
                        },
                      ],
                    }));
                    setActiveQuestionIdx(form.quiz_questions.length);
                  }}
                >
                  <IconPlus size={14} />
                </button>
                <button
                  type="button"
                  className="quiz-nav-arrow"
                  onClick={() =>
                    setActiveQuestionIdx(i =>
                      Math.min(form.quiz_questions.length - 1, i + 1)
                    )
                  }
                  disabled={
                    activeQuestionIdx === form.quiz_questions.length - 1
                  }
                >
                  <IconChevronRight size={14} />
                </button>
              </div>

              {/* Passing score + cooldown */}
              <div className="quiz-settings">
                <div className="quiz-setting-field">
                  <label className="quiz-setting-label">
                    Passing Score (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="quiz-setting-input"
                    value={form.quiz_passing_score}
                    onChange={e =>
                      setForm(p => ({
                        ...p,
                        quiz_passing_score: Math.min(
                          100,
                          Math.max(0, parseInt(e.target.value) || 0)
                        ),
                      }))
                    }
                  />
                  <span className="quiz-setting-hint">
                    Min % to pass (0–100)
                  </span>
                </div>
                <div className="quiz-setting-field">
                  <label className="quiz-setting-label">
                    Cooldown (minutes)
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="quiz-setting-input"
                    value={form.quiz_cooldown_minutes}
                    onChange={e =>
                      setForm(p => ({
                        ...p,
                        quiz_cooldown_minutes: Math.max(
                          0,
                          parseInt(e.target.value) || 0
                        ),
                      }))
                    }
                  />
                  <span className="quiz-setting-hint">
                    Wait time after failing (0 = none)
                  </span>
                </div>
              </div>
            </div>
          )}

          {form.type !== "QUIZ" && (
            <textarea
              placeholder="Description (optional)"
              value={form.description}
              onChange={e =>
                setForm(p => ({ ...p, description: e.target.value }))
              }
              rows={2}
              style={{
                width: "100%",
                border: "1px solid #ced4da",
                borderRadius: 6,
                padding: "8px 12px",
                fontSize: "0.875rem",
                fontFamily: "inherit",
                color: "#1a1a1a",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          )}
        </div>
      )}

      {/* Bottom row: Add details | [checkboxes centered] | Cancel · Add Task */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 14,
        }}
      >
        {/* Left — hide toggle for QUIZ since answers are always shown */}
        {form.type !== "QUIZ" ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
            }}
          >
            <button
              onClick={() => setShowDetails(o => !o)}
              style={{
                background: "none",
                border: "none",
                color: "#3b82f6",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
                whiteSpace: "nowrap",
              }}
            >
              {showDetails ? "Hide details" : "Add details"}
            </button>
            {/* Existing file chips */}
            {form.existing_files &&
              form.existing_files.map((ef, idx) => (
                <span
                  key={ef.id ?? idx}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 8px",
                    borderRadius: 20,
                    background: "#f5f3ff",
                    border: "1px solid #c4b5fd",
                    color: "#7c3aed",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    width: "fit-content",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  }}
                  title={ef.file_name || "Attachment"}
                >
                  📎 {ef.file_name || "File"}
                  <button
                    type="button"
                    onClick={() =>
                      setForm(p => ({
                        ...p,
                        existing_files: p.existing_files.filter(
                          (_, i) => i !== idx
                        ),
                      }))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: "#9ca3af",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 13,
                      lineHeight: 1,
                      marginLeft: 2,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            {/* New file chip (if one is staged) */}
            {taskFiles.map((taskFile, idx) => (
              <span
                key={`${taskFile.name}-${idx}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  borderRadius: 20,
                  background: "#fef3c7",
                  border: "1px solid #fcd34d",
                  color: "#92400e",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  width: "fit-content",
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
                title={taskFile.name}
              >
                📎 {taskFile.name}
                <button
                  type="button"
                  onClick={() => {
                    setTaskFiles(prev => prev.filter((_, i) => i !== idx));
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#9ca3af",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 13,
                    lineHeight: 1,
                    marginLeft: 2,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {/* Add File button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: "none",
                border: "none",
                color: "#3b82f6",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                padding: 0,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              📎 Add File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={e => {
                const selectedFiles = Array.from(e.target.files || []);
                setTaskFiles(prev => [...prev, ...selectedFiles]);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
          </div>
        ) : (
          <span />
        )}

        {/* Center — only show for applicable types */}
        <Group gap="lg">
          {(form.type === "USER_APPROVE" || form.type === "TEXT_RESPONSE") && (
            <>
              <Checkbox
                label="Requires Image Upload"
                size="sm"
                checked={form.requires_image}
                onChange={() =>
                  setForm(p => ({ ...p, requires_image: !p.requires_image }))
                }
              />
              <Checkbox
                label="Requires Admin Approval"
                size="sm"
                checked={form.requires_admin_approval}
                onChange={() =>
                  setForm(p => ({
                    ...p,
                    requires_admin_approval: !p.requires_admin_approval,
                  }))
                }
              />
            </>
          )}
          {form.type === "UPLOAD" && (
            <Checkbox
              label="Requires Admin Approval"
              size="sm"
              checked={form.requires_admin_approval}
              onChange={() =>
                setForm(p => ({
                  ...p,
                  requires_admin_approval: !p.requires_admin_approval,
                }))
              }
            />
          )}
          {form.type === "PITCH_REVIEW" && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 10px",
                borderRadius: 20,
                background: "#fef2f2",
                border: "1px solid #fca5a5",
                color: "#dc2626",
                fontSize: "0.8125rem",
                fontWeight: 600,
              }}
            >
              <IconShieldCheck size={13} />
              Admin Approval Required
            </span>
          )}
        </Group>

        {/* Right */}
        <Group gap="xs">
          <Button size="xs" variant="subtle" color="gray" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="xs"
            onClick={handleSave}
            disabled={
              form.type === "VIDEO"
                ? !form.video_id
                : form.type === "QUIZ"
                  ? form.quiz_questions.every(q => !q.question.trim())
                  : !form.name.trim()
            }
            style={{ background: "#374151", border: "none" }}
          >
            {task ? "Update Task" : "Add Task"}
          </Button>
        </Group>
      </div>
    </div>
  );
}

// ─── Save Status Modal ────────────────────────────────────────────────────────
function SaveStatusModal({
  isOpen,
  onClose,
  onSelect,
  isSaving,
  isEditMode,
  getVerifiedJWT,
  initialGroupIds = [],
}) {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // Load groups and pre-populate when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedGroupIds([...initialGroupIds]);
      loadGroups();
    } else {
      setSelectedGroupIds([]);
      setGroups([]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const jwt = await getVerifiedJWT();
      // Single request returns all groups with rep_count included — no N+1 fetches
      const res = await fetch(
        getApiEndpoint("/organization/groups-with-counts"),
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      if (res.ok) {
        const r = await res.json();
        setGroups(r.data || []);
      }
    } catch (e) {
      /* silent */
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleGroup = id => {
    setSelectedGroupIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  // No groups selected → DRAFT; any selected → ACTIVE
  const handleConfirm = () => {
    const status = selectedGroupIds.length > 0 ? "ACTIVE" : "DRAFT";
    onSelect(status, selectedGroupIds);
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={isEditMode ? "Save Program" : "Create Program"}
      size="sm"
      centered
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Select groups to publish to. Leave all unchecked to save as a draft.
        </Text>

        {loadingGroups ? (
          <Flex align="center" justify="center" py="lg" gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading groups...
            </Text>
          </Flex>
        ) : groups.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No groups found
          </Text>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {groups.map(g => {
              const selected = selectedGroupIds.includes(g.id);
              const repCount = g.rep_count ?? 0;
              return (
                <button
                  key={g.id}
                  onClick={() => toggleGroup(g.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    border: `1.5px solid ${selected ? "#d0521f" : "#e5e7eb"}`,
                    borderRadius: 10,
                    background: selected ? "#fff4ef" : "#ffffff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s",
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      border: `2px solid ${selected ? "#d0521f" : "#d1d5db"}`,
                      background: selected ? "#d0521f" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {selected && <IconCircleCheck size={14} color="white" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {g.name}
                    </div>
                    {g.description && (
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginTop: 1,
                        }}
                      >
                        {g.description}
                      </div>
                    )}
                  </div>
                  {/* Rep count badge */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "2px 8px",
                      borderRadius: 20,
                      background: selected ? "#f8e3db" : "#f3f4f6",
                      color: selected ? "#953b16" : "#6b7280",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <IconUsers size={11} />
                    {repCount === null
                      ? "…"
                      : `${repCount} rep${repCount !== 1 ? "s" : ""}`}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <Stack gap="xs" mt={4}>
          <Button
            fullWidth
            loading={isSaving}
            onClick={handleConfirm}
            style={{
              background: selectedGroupIds.length > 0 ? "#d0521f" : "#374151",
              border: "none",
            }}
          >
            {selectedGroupIds.length > 0
              ? `Publish to ${selectedGroupIds.length} Group${selectedGroupIds.length > 1 ? "s" : ""}`
              : "Save as Draft"}
          </Button>
          <Button
            fullWidth
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

// ─── Delete Program Modal ─────────────────────────────────────────────────────
function DeleteProgramModal({
  isOpen,
  onClose,
  programName,
  onConfirm,
  deleting,
}) {
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="DELETE PROGRAM"
      size="sm"
      centered
    >
      <Stack gap="md">
        <Text>
          Are you sure you want to delete <strong>{programName}</strong>? This
          will also delete all tasks and cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button color="red" onClick={onConfirm} loading={deleting}>
            Delete Program
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── Sortable Task Item ───────────────────────────────────────────────────────
function SortableTaskItem({
  task,
  index,
  isEditing,
  editingTask,
  videos,
  loadingVideos,
  onSaveTask,
  onCancelEdit,
  onEditClick,
  onDeleteTask,
  getTaskTypeLabel,
}) {
  const dndId = task._dndKey ?? task.id ?? `local-${index}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dndId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={style}>
        <InlineTaskForm
          task={editingTask}
          videos={videos}
          loadingVideos={loadingVideos}
          onSave={onSaveTask}
          onCancel={onCancelEdit}
        />
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="task-item">
      <div className="task-main">
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: "grab",
            touchAction: "none",
            color: "#d1d5db",
            display: "flex",
            alignItems: "center",
            padding: "0 4px",
            flexShrink: 0,
          }}
          title="Drag to reorder"
        >
          <IconGripVertical size={16} />
        </div>

        <div className="task-checkbox">
          <span style={{ color: "#9ca3af", fontSize: 12 }}>{index + 1}</span>
        </div>

        <div className="task-content" style={{ cursor: "default" }}>
          <Text
            style={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.3,
            }}
          >
            {task.name}
          </Text>
          {task.description && (
            <Text
              style={{
                fontSize: "0.8125rem",
                color: "#6b7280",
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              {task.description}
            </Text>
          )}
          {/* Row 1: task type + approval/image badges */}
          <Group gap="xs" mt="xs" className="task-meta">
            {(() => {
              const cfg = TASK_TYPES.find(t => t.value === task.type) || {
                Icon: IconListCheck,
                color: "#6b7280",
                bg: "#f3f4f6",
                label: getTaskTypeLabel(task.type),
              };
              const Icon = cfg.Icon;
              return (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 9px",
                    borderRadius: 20,
                    background: cfg.bg,
                    border: `1px solid ${cfg.color}33`,
                    color: cfg.color,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  <Icon size={11} />
                  {cfg.label || getTaskTypeLabel(task.type)}
                </span>
              );
            })()}
            {task.requires_admin_approval && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 9px",
                  borderRadius: 20,
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  color: "#dc2626",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                <IconShieldCheck size={11} />
                Admin Approval
              </span>
            )}
            {task.requires_image && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 9px",
                  borderRadius: 20,
                  background: "#f9fafb",
                  border: "1px solid #d1d5db",
                  color: "#374151",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                <IconUpload size={11} />
                Image Required
              </span>
            )}
          </Group>
          {/* Row 2: video title + file attachment chips */}
          {(task.videoTitle ||
            task.file_name ||
            task.file_url ||
            task.files?.length > 0) && (
            <Group gap="xs" mt={4} style={{ flexWrap: "wrap" }}>
              {task.type === "VIDEO" && task.videoTitle && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 9px",
                    borderRadius: 20,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1d4ed8",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    width: "fit-content",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  }}
                  title={task.videoTitle}
                >
                  📹 {task.videoTitle}
                </span>
              )}
              {task.files?.length
                ? task.files.map((file, idx) => (
                    <span
                      key={file.id ?? `${file.file_name}-${idx}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 9px",
                        borderRadius: 20,
                        background: "#f5f3ff",
                        border: "1px solid #c4b5fd",
                        color: "#7c3aed",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        width: "fit-content",
                        maxWidth: "100%",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                      title={file.file_name || "Attachment"}
                    >
                      📎 {file.file_name || "Attachment"}
                    </span>
                  ))
                : (task.file_name || task.file_url) && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 9px",
                        borderRadius: 20,
                        background: "#f5f3ff",
                        border: "1px solid #c4b5fd",
                        color: "#7c3aed",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        width: "fit-content",
                        maxWidth: "100%",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                      title={task.file_name || "Attachment"}
                    >
                      📎 {task.file_name || "Attachment"}
                    </span>
                  )}
            </Group>
          )}
        </div>

        <Group gap="xs" style={{ flexShrink: 0 }}>
          <Button
            size="xs"
            variant="subtle"
            color="orange"
            leftSection={<IconEdit size={14} />}
            onClick={() => onEditClick(task)}
          >
            Edit
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={() => onDeleteTask(task, index)}
          >
            Remove
          </Button>
        </Group>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function ProgramFormPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [programData, setProgramData] = useState({
    id: "",
    name: "",
    cover_image: null,
  });
  const [tasks, setTasks] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coverImageMessage, setCoverImageMessage] = useState("");
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [currentGroupIds, setCurrentGroupIds] = useState([]);

  // ─── Drag and drop sensors ────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tasks.findIndex(
      t => (t._dndKey ?? t.id ?? `local-${tasks.indexOf(t)}`) === active.id
    );
    const newIndex = tasks.findIndex(
      t => (t._dndKey ?? t.id ?? `local-${tasks.indexOf(t)}`) === over.id
    );
    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistic UI update
    const reordered = arrayMove(tasks, oldIndex, newIndex);
    setTasks(reordered);

    // Persist to API in edit mode (only for tasks with a real numeric id)
    if (isEditMode) {
      const movedTask = tasks[oldIndex];
      const realId =
        movedTask.id && !String(movedTask.id).startsWith("temp-")
          ? movedTask.id
          : null;
      if (realId) {
        try {
          const jwt = await getVerifiedJWT();
          const res = await fetch(
            getApiEndpoint("/v1/programs/tasks/sort-order"),
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              body: JSON.stringify({
                task_id: realId,
                sort_order: newIndex + 1,
              }),
            }
          );
          if (!res.ok) {
            // Revert on failure
            setTasks(tasks);
          }
        } catch (e) {
          setTasks(tasks);
        }
      }
    }
  };

  useEffect(() => {
    if (isEditMode) {
      // Parallelize both fetches — they are independent
      Promise.all([loadVideos(), fetchProgramData()]);
    } else {
      loadVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadVideos = async () => {
    try {
      setLoadingVideos(true);
      const jwt = await getVerifiedJWT();
      const res = await fetch(getApiEndpoint("/v1/training/videos/owned"), {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.ok) {
        const r = await res.json();
        setVideos(r.data || []);
      }
    } catch (e) {
      /* silent */
    } finally {
      setLoadingVideos(false);
    }
  };

  const fetchProgramData = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      // Direct single-program endpoint — much faster than fetching all programs
      const res = await fetch(
        getApiEndpoint(`/v1/programs/manage/details/${id}`),
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch program");
      const result = await res.json();
      const program = result.data;
      if (program) {
        setProgramData({
          id: program.id,
          name: program.program_name,
          cover_image: program.cover_image || null,
        });
        // Extract currently-assigned group IDs
        const assignedGroupIds = (program.group_programs || [])
          .map(gp => gp.group_id || gp.group?.id || gp.id)
          .filter(Boolean);
        setCurrentGroupIds(assignedGroupIds);
        setTasks(
          (program.tasks || []).map(t => ({
            _dndKey: t.id, // stable key for drag-and-drop
            id: t.id,
            name: t.task_name,
            description: t.task_desc || "",
            type: t.task_type,
            video_id: t.training_video_id ? t.training_video_id.toString() : "",
            videoTitle: t.training_video?.video_title || "",
            requires_image: t.requires_image || false,
            requires_admin_approval: t.requires_admin_approval || false,
            sort_order: t.sort_order,
            quiz_question: t.quiz_question || "",
            quiz_answers: t.quiz_answers || [],
            quiz_correct_answer: t.quiz_correct_answer ?? 0,
            file_url: t.file_url || null,
            file_name: t.file_name || null,
            // Multi-file support: files array from program_task_files
            files: Array.isArray(t.files)
              ? t.files.filter(f => !f.deleted_at)
              : [],
          }))
        );
      } else {
        setErrors({ general: "Program not found" });
      }
    } catch (e) {
      setErrors({ general: e.message });
    } finally {
      setLoading(false);
    }
  };

  const uploadCoverImage = async file => {
    if (!programData.id) return;
    setCoverImageMessage("Saving...");
    try {
      const jwt = await getVerifiedJWT();
      const formData = new FormData();
      formData.append("program_id", programData.id);
      formData.append("cover_image", file);
      const res = await fetch(getApiEndpoint("/v1/programs/cover-image"), {
        method: "PUT",
        headers: { Authorization: `Bearer ${jwt}` },
        body: formData,
      });
      if (res.ok) {
        setCoverImageMessage("✓ Cover image updated");
        setTimeout(() => setCoverImageMessage(""), 3000);
      } else {
        setCoverImageMessage("✗ Failed");
        setTimeout(() => setCoverImageMessage(""), 3000);
      }
    } catch (e) {
      setCoverImageMessage("✗ Network error");
      setTimeout(() => setCoverImageMessage(""), 3000);
    }
  };

  const handleSaveProgram = async (status = "DRAFT", groupIds = []) => {
    setErrors({});
    if (!programData.name.trim()) {
      setErrors({ name: "Program name is required" });
      setShowSaveModal(false);
      return;
    }
    setIsSaving(true);
    try {
      const jwt = await getVerifiedJWT();
      let targetId = isEditMode ? parseInt(id) : null;

      if (isEditMode) {
        const res = await fetch(getApiEndpoint("/v1/programs/edit"), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            program_id: parseInt(id),
            name: programData.name.trim(),
          }),
        });
        if (!res.ok) throw new Error("Failed to update program");
      } else {
        const res = await fetch(getApiEndpoint("/v1/programs/manager/add"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ name: programData.name.trim() }),
        });
        if (!res.ok) throw new Error("Failed to create program");
        const result = await res.json();
        targetId = result.data.id;
        if (programData.cover_image instanceof File) {
          const formData = new FormData();
          formData.append("program_id", targetId);
          formData.append("cover_image", programData.cover_image);
          await fetch(getApiEndpoint("/v1/programs/cover-image"), {
            method: "PUT",
            headers: { Authorization: `Bearer ${jwt}` },
            body: formData,
          });
        }
        for (const task of tasks) {
          const body = {
            program_id: targetId,
            task_name: task.name.trim(),
            task_desc: task.description ? task.description.trim() : "",
            task_type: task.type,
            requires_image: task.requires_image || false,
            requires_admin_approval: task.requires_admin_approval || false,
          };
          if (task.type === "VIDEO" && task.video_id)
            body.video_id = parseInt(task.video_id);
          if (task.type === "QUIZ") {
            body.quiz_question = task.quiz_question || "";
            body.quiz_answers = task.quiz_answers || [];
            body.quiz_correct_answer = task.quiz_correct_answer ?? 0;
            body.quiz_questions = task.quiz_questions || null;
            body.quiz_passing_score = task.quiz_passing_score ?? 75;
            body.quiz_cooldown_minutes = task.quiz_cooldown_minutes ?? 0;
          }
          if (task.files?.length) {
            const fd = new FormData();
            Object.entries(body).forEach(([k, v]) => {
              if (v !== undefined && v !== null)
                fd.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
            });
            task.files.forEach(file => fd.append("task_files", file));
            await fetch(getApiEndpoint("/v1/programs/tasks/add"), {
              method: "POST",
              headers: { Authorization: `Bearer ${jwt}` },
              body: fd,
            });
          } else {
            await fetch(getApiEndpoint("/v1/programs/tasks/add"), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              body: JSON.stringify(body),
            });
          }
        }
      }

      // Apply the chosen status
      if (targetId) {
        await fetch(getApiEndpoint("/v1/programs/status"), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ program_id: targetId, status }),
        });

        // Assign/unassign groups based on diff from initial state
        const toAdd = groupIds.filter(gid => !currentGroupIds.includes(gid));
        const toRemove = currentGroupIds.filter(gid => !groupIds.includes(gid));
        const groupChanges = [
          ...toAdd.map(gid => ({ group_id: gid, include: true })),
          ...toRemove.map(gid => ({ group_id: gid, include: false })),
        ];
        if (groupChanges.length > 0) {
          await Promise.allSettled(
            groupChanges.map(({ group_id, include }) =>
              fetch(getApiEndpoint("/v1/groups/group/program"), {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify({
                  group_id,
                  program_id: targetId,
                  include,
                }),
              })
            )
          );
        }
      }

      navigate("/admin?tab=programs");
    } catch (e) {
      setErrors({ general: e.message });
    } finally {
      setIsSaving(false);
      setShowSaveModal(false);
    }
  };

  const handleSaveTask = taskData => {
    // Optimistic update — close form and update UI immediately
    if (isEditMode && editingTaskId) {
      // Compute files that were removed (had a real id but are no longer in existing_files)
      const originalTask = tasks.find(t => t.id === editingTaskId);
      const originalFiles = originalTask?.files || [];
      const keptFileIds = new Set(
        (taskData.existing_files || []).filter(f => f.id).map(f => f.id)
      );
      const removedFileIds = originalFiles
        .filter(f => f.id && !keptFileIds.has(f.id))
        .map(f => f.id);

      // Optimistic state: updated files list after removes + new file
      const optimisticFiles = [
        ...(taskData.existing_files || []).filter(f => f.id),
        ...(taskData.files || []).map(file => ({
          id: null,
          file_name: file.name,
          file_url: null,
        })),
      ];
      const updated_file_name =
        optimisticFiles.length > 0
          ? optimisticFiles[optimisticFiles.length - 1].file_name
          : null;
      const updated_file_url =
        optimisticFiles.length > 0
          ? optimisticFiles[optimisticFiles.length - 1].file_url
          : null;

      setTasks(prev =>
        prev.map(t =>
          t.id === editingTaskId
            ? {
                ...t,
                ...taskData,
                file_name: updated_file_name,
                file_url: updated_file_url,
                files: optimisticFiles,
              }
            : t
        )
      );

      // Delete removed files in background
      if (removedFileIds.length > 0) {
        getVerifiedJWT().then(jwt => {
          Promise.allSettled(
            removedFileIds.map(fileId =>
              fetch(
                getApiEndpoint(
                  `/v1/programs/tasks/${editingTaskId}/files/${fileId}`
                ),
                {
                  method: "DELETE",
                  headers: { Authorization: `Bearer ${jwt}` },
                }
              )
            )
          );
        });
      }
      // Fire API in background
      getVerifiedJWT()
        .then(jwt => {
          const body = {
            task_id: editingTaskId,
            task_name: taskData.name.trim(),
            task_desc: taskData.description ? taskData.description.trim() : "",
            task_type: taskData.type,
            requires_image: taskData.requires_image || false,
            requires_admin_approval: taskData.requires_admin_approval || false,
          };
          if (taskData.type === "VIDEO" && taskData.video_id)
            body.video_id = parseInt(taskData.video_id);
          if (taskData.type === "QUIZ") {
            body.quiz_question = taskData.quiz_question || "";
            body.quiz_answers = taskData.quiz_answers || [];
            body.quiz_correct_answer = taskData.quiz_correct_answer ?? 0;
            body.quiz_questions = taskData.quiz_questions || null;
            body.quiz_passing_score = taskData.quiz_passing_score ?? 75;
            body.quiz_cooldown_minutes = taskData.quiz_cooldown_minutes ?? 0;
          }
          (taskData.files?.length
            ? (() => {
                const fd = new FormData();
                Object.entries(body).forEach(([k, v]) => {
                  if (v !== undefined && v !== null)
                    fd.append(
                      k,
                      Array.isArray(v) ? JSON.stringify(v) : String(v)
                    );
                });
                taskData.files.forEach(file => fd.append("task_files", file));
                return fetch(getApiEndpoint("/v1/programs/tasks/update"), {
                  method: "PUT",
                  headers: { Authorization: `Bearer ${jwt}` },
                  body: fd,
                });
              })()
            : fetch(getApiEndpoint("/v1/programs/tasks/update"), {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify(body),
              })
          ).catch(() => {
            /* silent */
          });
        })
        .catch(() => {
          /* silent */
        });
    } else if (isEditMode && programData.id) {
      // Optimistically add with a temp id, then replace with real id from API
      const tempId = `temp-${Date.now()}`;
      setTasks(prev => [...prev, { ...taskData, id: tempId }]);
      getVerifiedJWT()
        .then(jwt => {
          const body = {
            program_id: programData.id,
            task_name: taskData.name.trim(),
            task_desc: taskData.description ? taskData.description.trim() : "",
            task_type: taskData.type,
            requires_image: taskData.requires_image || false,
            requires_admin_approval: taskData.requires_admin_approval || false,
          };
          if (taskData.type === "VIDEO" && taskData.video_id)
            body.video_id = parseInt(taskData.video_id);
          if (taskData.type === "QUIZ") {
            body.quiz_question = taskData.quiz_question || "";
            body.quiz_answers = taskData.quiz_answers || [];
            body.quiz_correct_answer = taskData.quiz_correct_answer ?? 0;
            body.quiz_questions = taskData.quiz_questions || null;
            body.quiz_passing_score = taskData.quiz_passing_score ?? 75;
            body.quiz_cooldown_minutes = taskData.quiz_cooldown_minutes ?? 0;
          }
          (taskData.files?.length
            ? (() => {
                const fd = new FormData();
                Object.entries(body).forEach(([k, v]) => {
                  if (v !== undefined && v !== null)
                    fd.append(
                      k,
                      Array.isArray(v) ? JSON.stringify(v) : String(v)
                    );
                });
                taskData.files.forEach(file => fd.append("task_files", file));
                return fetch(getApiEndpoint("/v1/programs/tasks/add"), {
                  method: "POST",
                  headers: { Authorization: `Bearer ${jwt}` },
                  body: fd,
                });
              })()
            : fetch(getApiEndpoint("/v1/programs/tasks/add"), {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${jwt}`,
                },
                body: JSON.stringify(body),
              })
          )
            .then(res => {
              if (res.ok) {
                res
                  .json()
                  .then(r => {
                    // Replace temp id with real id
                    setTasks(prev =>
                      prev.map(t =>
                        t.id === tempId ? { ...t, id: r.data.id } : t
                      )
                    );
                  })
                  .catch(() => {
                    /* silent */
                  });
              }
            })
            .catch(() => {
              /* silent */
            });
        })
        .catch(() => {
          /* silent */
        });
    } else {
      // New program (not yet saved) — just update local state
      if (
        editingTaskId !== null &&
        typeof editingTaskId === "number" &&
        !isEditMode
      ) {
        setTasks(prev =>
          prev.map((t, i) => (i === editingTaskId ? taskData : t))
        );
      } else {
        setTasks(prev => [...prev, taskData]);
      }
    }
    setShowTaskForm(false);
    setEditingTask(null);
    setEditingTaskId(null);
  };

  const handleDeleteTask = (task, index) => {
    // Optimistic update — remove from UI immediately
    setTasks(prev => prev.filter((_, i) => i !== index));
    // Fire API in background if in edit mode
    if (isEditMode && task.id) {
      getVerifiedJWT()
        .then(jwt => {
          fetch(getApiEndpoint(`/v1/programs/task/${task.id}`), {
            method: "DELETE",
            headers: { Authorization: `Bearer ${jwt}` },
          }).catch(() => {
            /* silent */
          });
        })
        .catch(() => {
          /* silent */
        });
    }
  };

  const handleDeleteProgram = async () => {
    setDeleting(true);
    try {
      const jwt = await getVerifiedJWT();
      await fetch(getApiEndpoint(`/v2/programs/${id}`), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${jwt}` },
      });
      navigate("/admin?tab=programs");
    } catch (e) {
      setErrors({ general: "Failed to delete program" });
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getTaskTypeLabel = type => {
    switch (type) {
      case "VIDEO":
        return "Watch Video";
      case "UPLOAD":
        return "Upload File";
      case "USER_APPROVE":
        return "Self-Complete";
      case "ADMIN_APPROVE":
        return "Admin Approval";
      case "TEXT_RESPONSE":
        return "Text Response";
      case "PITCH_REVIEW":
        return "Pitch Review";
      case "QUIZ":
        return "Quiz";
      default:
        return type;
    }
  };

  const getThumbnailUrl = img => {
    if (!img) return null;
    if (img instanceof File) return URL.createObjectURL(img);
    return img;
  };

  // Task type breakdown
  const taskTypeCounts = tasks.reduce((acc, t) => {
    acc[t.type] = (acc[t.type] || 0) + 1;
    return acc;
  }, {});

  const taskTypeConfig = [
    {
      type: "VIDEO",
      label: "Watch Video",
      icon: <IconPlayerPlay size={16} />,
      color: "#3b82f6",
      bg: "#eff6ff",
    },
    {
      type: "USER_APPROVE",
      label: "Self-Complete",
      icon: <IconUserCheck size={16} />,
      color: "#22c55e",
      bg: "#f0fdf4",
    },
    {
      type: "TEXT_RESPONSE",
      label: "Text Response",
      icon: <IconFileText size={16} />,
      color: "#8b5cf6",
      bg: "#f5f3ff",
    },
    {
      type: "PITCH_REVIEW",
      label: "Pitch Review",
      icon: <IconStar size={16} />,
      color: "#d946ef",
      bg: "#fdf4ff",
    },
    {
      type: "UPLOAD",
      label: "Upload File",
      icon: <IconUpload size={16} />,
      color: "#f59e0b",
      bg: "#fef3c7",
    },
    {
      type: "QUIZ",
      label: "Quiz",
      icon: <IconQuestionMark size={16} />,
      color: "#06b6d4",
      bg: "#ecfeff",
    },
  ];

  const totalCount = tasks.length;

  const adminHeader = (
    <div className="org-page-custom-header">
      <div className="org-page-header-left">
        <h1 className="org-page-main-title">Admin</h1>
        <nav className="org-page-nav-tabs" role="tablist">
          {ADMIN_SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = section.id === "program-creation";
            return (
              <button
                key={section.id}
                role="tab"
                aria-selected={isActive}
                className={`org-page-nav-tab${isActive ? " active" : ""}`}
                onClick={() => section.path && navigate(section.path)}
              >
                <Icon size={16} />
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageContent customHeader={adminHeader}>
        <Box p="xl" ta="center">
          <Text c="dimmed">Loading program...</Text>
        </Box>
      </PageContent>
    );
  }

  return (
    <PageContent customHeader={adminHeader}>
      <div className="program-details-container" style={{ paddingTop: 0 }}>
        {errors.general && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="red"
            variant="light"
            mb="md"
          >
            {errors.general}
          </Alert>
        )}

        {/* ── Two-column layout ── */}
        <div className="program-details-layout">
          {/* ── Left Sidebar ── */}
          <div className="program-sidebar">
            {/* Program Name */}
            <TextInput
              value={programData.name}
              onChange={e => {
                setProgramData(p => ({ ...p, name: e.target.value }));
                setErrors({});
              }}
              placeholder="Program name..."
              error={errors.name}
              mb="sm"
              styles={{
                input: {
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  border: "none",
                  borderBottom: "2px solid #e5e7eb",
                  borderRadius: 0,
                  padding: "4px 0",
                  background: "transparent",
                  height: "auto",
                },
              }}
            />

            {/* Cover Image — 16:9, full sidebar width */}
            <FileButton
              onChange={file => {
                if (file) {
                  setProgramData(p => ({ ...p, cover_image: file }));
                  if (isEditMode && programData.id) uploadCoverImage(file);
                }
              }}
              accept="image/*"
            >
              {props => (
                <div
                  {...props}
                  style={{
                    width: "100%",
                    paddingTop: "56.25%",
                    position: "relative",
                    borderRadius: 10,
                    border: "2px dashed #e5e7eb",
                    overflow: "hidden",
                    cursor: "pointer",
                    background: programData.cover_image
                      ? "transparent"
                      : "#f9fafb",
                    marginBottom: 8,
                  }}
                >
                  {programData.cover_image ? (
                    <>
                      <img
                        src={getThumbnailUrl(programData.cover_image)}
                        alt="Cover"
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: "rgba(0,0,0,0.45)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: 0,
                          transition: "opacity 0.2s",
                        }}
                        onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
                        onMouseLeave={e => (e.currentTarget.style.opacity = 0)}
                      >
                        <div style={{ textAlign: "center" }}>
                          <IconUpload size={18} color="white" />
                          <Text c="white" fw={600} size="xs" mt={4}>
                            Change Image
                          </Text>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                      }}
                    >
                      <IconUpload size={22} stroke={1.5} color="#9ca3af" />
                      <Text c="dimmed" size="xs" fw={500}>
                        Add cover image
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </FileButton>
            {coverImageMessage && (
              <Text
                size="xs"
                mb="sm"
                c={
                  coverImageMessage.includes("✓")
                    ? "green"
                    : coverImageMessage.includes("✗")
                      ? "red"
                      : "dimmed"
                }
              >
                {coverImageMessage}
              </Text>
            )}

            {/* Total Tasks Card */}
            <div
              className="progress-card"
              style={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Group mb="lg">
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: "#fff7ed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconListCheck size={22} color="#d0521f" />
                </div>
                <div>
                  <Text
                    style={{
                      fontSize: "2rem",
                      fontWeight: 700,
                      color: "#1a1a1a",
                      lineHeight: 1,
                    }}
                  >
                    {totalCount}
                  </Text>
                  <Text
                    style={{
                      fontSize: "0.9375rem",
                      color: "#666",
                      fontWeight: 500,
                    }}
                  >
                    Total Tasks
                  </Text>
                </div>
              </Group>

              {/* Task type breakdown */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {taskTypeConfig.map(cfg => {
                  const count = taskTypeCounts[cfg.type] || 0;
                  return (
                    <div
                      key={cfg.type}
                      className="insight-row"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <Group gap="xs">
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: cfg.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: cfg.color,
                          }}
                        >
                          {cfg.icon}
                        </div>
                        <Text className="insight-label">{cfg.label}</Text>
                      </Group>
                      <span className="insight-badge">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Right: Tasks Panel ── */}
          <div className="program-content">
            <div
              className="tasks-list"
              style={{
                flex: 1,
                overflowY: "scroll",
                overflowX: "hidden",
                minHeight: 0,
                paddingRight: 4,
                maxHeight: "100%",
              }}
            >
              {tasks.length === 0 && (
                <Paper p="xl" ta="center" withBorder className="empty-state">
                  <Text size="lg" fw={600} mb="xs">
                    No tasks yet
                  </Text>
                  <Text size="sm" c="dimmed">
                    Add tasks to define what reps need to complete in this
                    program
                  </Text>
                </Paper>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={tasks.map((t, i) => t._dndKey ?? t.id ?? `local-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {tasks.map((task, index) => {
                    const isEditing =
                      editingTaskId === (task.id || index) && showTaskForm;
                    return (
                      <SortableTaskItem
                        key={task._dndKey ?? task.id ?? index}
                        task={task}
                        index={index}
                        isEditing={isEditing}
                        editingTask={isEditing ? editingTask : null}
                        videos={videos}
                        loadingVideos={loadingVideos}
                        onSaveTask={handleSaveTask}
                        onCancelEdit={() => {
                          setShowTaskForm(false);
                          setEditingTask(null);
                          setEditingTaskId(null);
                        }}
                        onEditClick={t => {
                          setEditingTask(t);
                          setEditingTaskId(t.id || index);
                          setShowTaskForm(true);
                        }}
                        onDeleteTask={handleDeleteTask}
                        getTaskTypeLabel={getTaskTypeLabel}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>

              {/* Inline Add Task form — shown inside scroll area when adding new */}
              {showTaskForm && !editingTaskId && (
                <InlineTaskForm
                  videos={videos}
                  loadingVideos={loadingVideos}
                  onSave={handleSaveTask}
                  onCancel={() => {
                    setShowTaskForm(false);
                    setEditingTask(null);
                    setEditingTaskId(null);
                  }}
                />
              )}
            </div>

            {/* Sticky bottom actions — Add Task + Save/Delete */}
            <div
              style={{
                padding: "16px 24px 24px",
                borderTop: "1px solid #f3f4f6",
                flexShrink: 0,
              }}
            >
              <Stack gap="sm">
                {!showTaskForm && (
                  <Button
                    fullWidth
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      setEditingTask(null);
                      setEditingTaskId(null);
                      setShowTaskForm(true);
                    }}
                    style={{
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderColor: "#e5e7eb",
                      background: "#f9fafb",
                      color: "#6b7280",
                      height: 44,
                    }}
                  >
                    Add Task
                  </Button>
                )}
                <Button
                  fullWidth
                  onClick={() => {
                    if (!programData.name.trim()) {
                      setErrors({ name: "Program name is required" });
                      return;
                    }
                    setShowSaveModal(true);
                  }}
                  loading={isSaving}
                  style={{
                    background: "#d0521f",
                    border: "none",
                    height: 48,
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  {isEditMode ? "Save Program" : "Create Program"}
                </Button>
              </Stack>
            </div>
          </div>
        </div>

        <DeleteProgramModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          programName={programData.name}
          onConfirm={handleDeleteProgram}
          deleting={deleting}
        />

        <SaveStatusModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSelect={handleSaveProgram}
          isSaving={isSaving}
          isEditMode={isEditMode}
          getVerifiedJWT={getVerifiedJWT}
          initialGroupIds={currentGroupIds}
        />
      </div>
    </PageContent>
  );
}

export default ProgramFormPage;
